import { buildSearchQuery, getOriginalQueryFromUrl } from '@ao/search-synonyms';

const ROOT = window.Shopify?.routes?.root || '/';
const FILTER_PREFIXES = ['filter.', 'filter%5B'];

const debounce = (fn, wait = 180) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
};

/*
 * The visible URL keeps the customer's own words. Synonym expansion is only
 * added when the plain query is unlikely to match on its own, so shared links
 * stay readable and prefix matching still applies to what was actually typed.
 */
function searchUrl(rawQuery, options = {}) {
  const url = new URL(`${ROOT}search`, window.location.origin);
  url.searchParams.set('type', 'product');
  url.searchParams.set('q', options.expand ? buildSearchQuery(rawQuery) : rawQuery);
  url.searchParams.set('aoq', rawQuery);
  url.searchParams.set('options[prefix]', 'last');
  return url;
}

function isInternalCatalogQuery(value = '') {
  return value === 'ao_catalog_all' || value === 'ao_catalog_shirts' || value === 'ao_catalog_art';
}

function originalQuery() {
  const explicit = getOriginalQueryFromUrl();
  if (explicit && !isInternalCatalogQuery(explicit)) return explicit;
  const q = new URL(window.location.href).searchParams.get('q') || '';
  if (isInternalCatalogQuery(q)) return '';
  return /\sOR\s/i.test(q) ? '' : q;
}

function syncSearchPageCopy(scope = document) {
  const raw = originalQuery();
  scope.querySelectorAll('[data-ao-original-query-input]').forEach((input) => {
    if (raw) input.value = raw;
  });
  scope.querySelectorAll('[data-ao-search-summary]').forEach((node) => {
    node.textContent = raw
      ? `Výsledky pre „${raw}“. Synonymá a skratky vyhodnocujeme iba na pozadí.`
      : 'Nájdi produkt podľa auta, motívu alebo textu. Zobrazené sú iba relevantné výsledky.';
  });
}

function safeImage(product) {
  return product?.featured_image?.url || product?.featured_image || product?.image || '';
}

function track(eventName, detail = {}) {
  window.AOAnalytics?.track?.(eventName, detail);
}

function renderSuggestions(container, products, rawQuery) {
  container.replaceChildren();
  container.setAttribute('role', 'listbox');

  if (!products.length) {
    const empty = document.createElement('div');
    empty.className = 'ao-collection-suggestions-empty';
    empty.setAttribute('role', 'status');
    empty.textContent = 'Nenašli sme presnú zhodu. Skús značku, model alebo časť textu.';
    container.appendChild(empty);
    track('search_zero_results', { query: rawQuery });
  } else {
    products.slice(0, 4).forEach((product, index) => {
      const link = document.createElement('a');
      link.className = 'ao-collection-suggestion';
      link.href = product.url || '#';
      link.id = `${container.id}-option-${index}`;
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      link.dataset.aoSuggestionOption = 'true';
      link.dataset.aoSuggestionTitle = product.title || 'Produkt';

      const imageUrl = safeImage(product);
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        link.appendChild(img);
      } else {
        const fallback = document.createElement('span');
        fallback.className = 'ao-collection-suggestion__fallback';
        fallback.textContent = 'AO';
        fallback.setAttribute('aria-hidden', 'true');
        link.appendChild(fallback);
      }

      const copy = document.createElement('span');
      const title = document.createElement('strong');
      title.textContent = product.title || 'Produkt';
      const meta = document.createElement('small');
      meta.textContent = product.type || 'AUTOOBRAZY';
      copy.append(title, meta);

      const arrow = document.createElement('span');
      arrow.className = 'ao-collection-suggestion__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '›';
      link.append(copy, arrow);
      container.appendChild(link);
    });
  }

  const all = document.createElement('a');
  all.className = 'ao-collection-suggestions-all';
  all.href = searchUrl(rawQuery).toString();
  all.id = `${container.id}-option-all`;
  all.setAttribute('role', 'option');
  all.setAttribute('aria-selected', 'false');
  all.dataset.aoSuggestionOption = 'true';
  all.textContent = `Zobraziť všetky výsledky pre „${rawQuery}“`;
  container.appendChild(all);
  container.hidden = false;
}

function initSearch(form) {
  if (form.dataset.aoSearchReady === 'true') return;
  form.dataset.aoSearchReady = 'true';
  const input = form.querySelector('[data-ao-collection-search-input]');
  const suggestions = form.querySelector('[data-ao-collection-search-suggestions]');
  if (!input) return;

  const rawFromUrl = originalQuery();
  if (input.hasAttribute('data-ao-original-query-input') && rawFromUrl) input.value = rawFromUrl;

  form.addEventListener('submit', (event) => {
    const raw = input.value.trim();
    if (!raw) return;
    event.preventDefault();
    track('search_query', { query: raw, source: 'collection_search' });
    window.location.assign(searchUrl(raw).toString());
  });

  if (!suggestions) return;
  if (!suggestions.id) suggestions.id = `AO-Suggestions-${Math.random().toString(36).slice(2, 9)}`;
  suggestions.setAttribute('role', 'listbox');
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-controls', suggestions.id);
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('autocomplete', 'off');

  let controller = null;
  let activeIndex = -1;

  const options = () => [...suggestions.querySelectorAll('[data-ao-suggestion-option]')];
  const setOpen = (open) => {
    const shouldOpen = !!open && suggestions.childElementCount > 0;
    suggestions.hidden = !shouldOpen;
    input.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    if (!shouldOpen) {
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      options().forEach((option) => option.setAttribute('aria-selected', 'false'));
    }
  };

  const setActive = (nextIndex) => {
    const items = options();
    if (!items.length) return;
    activeIndex = (nextIndex + items.length) % items.length;
    items.forEach((item, index) => item.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false'));
    input.setAttribute('aria-activedescendant', items[activeIndex].id);
    items[activeIndex].scrollIntoView({ block: 'nearest' });
  };

  const lookup = debounce(async () => {
    const raw = input.value.trim();
    activeIndex = -1;
    input.removeAttribute('aria-activedescendant');
    if (raw.length < 2) {
      setOpen(false);
      suggestions.replaceChildren();
      controller?.abort();
      return;
    }

    controller?.abort();
    controller = new AbortController();
    const params = new URLSearchParams();
    params.set('q', buildSearchQuery(raw));
    params.set('resources[type]', 'product');
    params.set('resources[limit]', '4');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', 'title,product_type,variants.title,tag');

    try {
      const response = await fetch(`${ROOT}search/suggest.json?${params.toString()}`, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const payload = await response.json();
      const products = payload?.resources?.results?.products || [];
      renderSuggestions(suggestions, products, raw);
      setOpen(true);
      track('search_suggestions_loaded', { query: raw, results: products.length });
    } catch (error) {
      if (error?.name !== 'AbortError') {
        setOpen(false);
        track('search_suggestions_failed', { query: raw, status: error?.message || 'unknown' });
      }
    }
  }, 170);

  input.addEventListener('input', lookup);
  input.addEventListener('focus', () => {
    if (suggestions.childElementCount && input.value.trim().length >= 2) setOpen(true);
  });
  input.addEventListener('keydown', (event) => {
    const items = options();
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!items.length || suggestions.hidden) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(activeIndex + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(activeIndex <= 0 ? items.length - 1 : activeIndex - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(items.length - 1);
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      items[activeIndex].click();
    }
  });

  suggestions.addEventListener('pointermove', (event) => {
    const item = event.target.closest?.('[data-ao-suggestion-option]');
    if (!item) return;
    const index = options().indexOf(item);
    if (index >= 0) setActive(index);
  });
  suggestions.addEventListener('click', (event) => {
    const item = event.target.closest?.('[data-ao-suggestion-option]');
    if (!item) return;
    track('search_suggestion_clicked', { query: input.value.trim(), title: item.dataset.aoSuggestionTitle || item.textContent.trim().slice(0, 120) });
  });
  document.addEventListener('pointerdown', (event) => {
    if (!form.contains(event.target)) setOpen(false);
  });
}

function initMiniSearch(form) {
  if (!form || form.dataset.aoMiniReady === 'true') return;
  form.dataset.aoMiniReady = 'true';
  const input = form.querySelector('[data-ao-mini-search-input]');
  if (!input) return;
  const raw = originalQuery();
  if (raw) input.value = raw;
  form.addEventListener('submit', (event) => {
    const q = input.value.trim();
    if (!q) return;
    event.preventDefault();
    track('search_query', { query: q, source: 'mini_search' });
    window.location.assign(searchUrl(q).toString());
  });
}

function clearFilterParams(url) {
  [...url.searchParams.keys()].forEach((key) => {
    if (FILTER_PREFIXES.some((prefix) => key.startsWith(prefix))) url.searchParams.delete(key);
  });
  url.searchParams.delete('page');
  return url;
}

function clearFilters() {
  const url = clearFilterParams(new URL(window.location.href));
  window.location.assign(url.toString());
}

let lockedScrollY = 0;

function lockPageForDrawer() {
  const html = document.documentElement;
  if (html.classList.contains('ao-browser-lock')) return;
  lockedScrollY = Math.max(0, window.scrollY || window.pageYOffset || 0);
  html.classList.add('ao-browser-lock');
  document.body.style.position = 'fixed';
  document.body.style.top = `-${lockedScrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
}

function unlockPageFromDrawer() {
  const html = document.documentElement;
  if (!html.classList.contains('ao-browser-lock')) return;
  html.classList.remove('ao-browser-lock');
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';
  window.scrollTo(0, lockedScrollY);
}

function openDrawer(drawer, backdrop) {
  document.querySelectorAll('.ao-browser-drawer.is-open').forEach((item) => {
    if (item !== drawer) item.classList.remove('is-open');
  });
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  backdrop.hidden = false;
  requestAnimationFrame(() => backdrop.classList.add('is-visible'));
  lockPageForDrawer();
}

function closeDrawers(root) {
  root.querySelectorAll('.ao-browser-drawer.is-open').forEach((drawer) => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
  });
  const backdrop = root.querySelector('[data-ao-browser-backdrop]');
  if (backdrop) {
    backdrop.classList.remove('is-visible');
    window.setTimeout(() => { if (!backdrop.classList.contains('is-visible')) backdrop.hidden = true; }, 210);
  }
  unlockPageFromDrawer();
}

function setStickyOffset() {
  const header = document.getElementById('header-group') || document.querySelector('[data-ao-header]');
  const offset = header ? Math.max(0, Math.round(header.getBoundingClientRect().height)) : 0;
  document.documentElement.style.setProperty('--ao-browser-sticky-top', `${offset}px`);
}

function initSticky(toolbar) {
  if (!toolbar || toolbar.dataset.aoStickyReady === 'true') return;
  toolbar.dataset.aoStickyReady = 'true';
  setStickyOffset();
  /* Výšku hlavičky meriame len pri zmene jej rozmerov, nie pri každom
     scroll frame — getBoundingClientRect() v scroll handleri vynucoval
     prepočet rozloženia na každom posune. */
  let ticking = false;
  const sync = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const top = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ao-browser-sticky-top')) || 0;
      const rect = toolbar.getBoundingClientRect();
      toolbar.classList.toggle('is-stuck', Math.abs(rect.top - top) < 3 && window.scrollY > 50);
      ticking = false;
    });
  };
  window.addEventListener('scroll', sync, { passive: true });
  /* `visualViewport.resize` tu bol zbytočný a škodlivý: pri štipkovom zoome
     sa spustí desiatky ráz za sekundu, hoci výška hlavičky sa nemení.
     Skutočnú zmenu rozmerov hlavičky pokrýva ResizeObserver nižšie. */
  const onViewportResize = window.AOViewport
    ? window.AOViewport.onResize
    : (fn) => window.addEventListener('resize', fn, { passive: true });
  onViewportResize(() => { setStickyOffset(); sync(); });
  const headerEl = document.getElementById('header-group') || document.querySelector('[data-ao-header]');
  if (headerEl && 'ResizeObserver' in window) {
    new ResizeObserver(() => { setStickyOffset(); sync(); }).observe(headerEl);
  }
}

function initControls(root) {
  if (!root || root.dataset.aoControlsReady === 'true') return;
  root.dataset.aoControlsReady = 'true';
  const backdrop = root.querySelector('[data-ao-browser-backdrop]');
  const filterDrawer = root.querySelector('[data-ao-filter-drawer]');
  const sortDrawer = root.querySelector('[data-ao-sort-drawer]');
  const form = root.querySelector('[data-ao-filter-form]');

  root.querySelector('[data-ao-open-filters]')?.addEventListener('click', () => openDrawer(filterDrawer, backdrop));
  root.querySelector('[data-ao-open-sort]')?.addEventListener('click', () => openDrawer(sortDrawer, backdrop));
  root.querySelectorAll('[data-ao-close-drawers]').forEach((button) => button.addEventListener('click', () => closeDrawers(root)));
  backdrop?.addEventListener('click', () => closeDrawers(root));
  root.querySelectorAll('[data-ao-clear-filters]').forEach((button) => button.addEventListener('click', clearFilters));

  const rawQuery = originalQuery();
  if (rawQuery) {
    root.querySelectorAll('.ao-browser-active a').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('aoq', rawQuery);
        link.href = url.toString();
      } catch (_) {}
    });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawers(root);
  });

  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    const url = clearFilterParams(new URL(window.location.href));
    const data = new FormData(form);
    for (const [name, value] of data.entries()) {
      if (String(value).length) url.searchParams.append(name, String(value));
    }
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  });

  root.querySelectorAll('[data-ao-sort-value]').forEach((button) => {
    button.addEventListener('click', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', button.dataset.aoSortValue || 'best-selling');
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    });
  });

  initMiniSearch(root.querySelector('[data-ao-mini-search]'));
  initSticky(root.querySelector('[data-ao-browser-sticky]'));
}

function initCard(card) {
  if (!card || card.dataset.aoCardReady === 'true') return;
  card.dataset.aoCardReady = 'true';

  const slides = card.querySelector('[data-ao-card-slides]');
  const corner = card.querySelector('[data-ao-card-corner]');
  const cornerImage = card.querySelector('[data-ao-card-corner-image]');
  if (!slides || slides.children.length < 2) return;

  // Roh vždy ukazuje tú fotku, ktorá práve nie je na karte.
  const paint = () => {
    if (!cornerImage) return;
    const onSecond = slides.scrollLeft > slides.clientWidth / 2;
    const next = onSecond ? cornerImage.dataset.first : cornerImage.dataset.second;
    if (next && cornerImage.src !== next) cornerImage.src = next;
  };

  corner?.addEventListener('click', (event) => {
    event.preventDefault();
    const onSecond = slides.scrollLeft > slides.clientWidth / 2;
    slides.scrollTo({ left: onSecond ? 0 : slides.clientWidth, behavior: 'smooth' });
    track('product_card_image_view', { product_id: card.dataset.productId || '', view: onSecond ? 1 : 2 });
  });

  let raf = 0;
  slides.addEventListener('scroll', () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(paint);
  }, { passive: true });

  paint();
}

function initPagination(root = document) {
  const pagination = root.querySelector('[data-ao-pagination]');
  if (!pagination || pagination.dataset.aoPaginationReady === 'true') return;
  pagination.dataset.aoPaginationReady = 'true';
  pagination.addEventListener('click', (event) => {
    const link = event.target.closest('a');
    if (link) track('catalog_page_change', { page: link.textContent.trim() || link.getAttribute('aria-label') || '' });
  });
}

/*
 * Synonym rescue: only when the customer's own words returned nothing do we
 * retry once with the expanded query. Everyday searches keep a clean URL and
 * precise relevance; dead ends still get a second chance.
 */
function rescueEmptySearch() {
  const context = window.AOPageContext;
  if (!context || typeof context.searchResults !== 'number' || context.searchResults > 0) return;

  const url = new URL(window.location.href);
  if (url.searchParams.get('aox') === '1') return; // already retried

  const raw = url.searchParams.get('aoq') || url.searchParams.get('q') || '';
  if (!raw.trim() || isInternalCatalogQuery(raw)) return;

  const expanded = buildSearchQuery(raw);
  if (!expanded || expanded === raw) return;

  const target = searchUrl(raw, { expand: true });
  target.searchParams.set('aox', '1');
  window.AOAnalytics?.track?.('search_synonym_rescue', { query: raw });
  window.location.replace(target.toString());
}

/* V122 — vynútené presmerovanie na ?sort_by=best-selling odstránené.
   Každé otvorenie kolekcie znamenalo druhé úplné načítanie stránky
   (viditeľné bliknutie, dvojnásobná záťaž, horšie LCP) a výsledná URL
   je navyše v Shopify robots.txt zakázaná na prechádzanie
   (Disallow: /collections/*sort_by*).
   Predvolené poradie produktov sa nastavuje na kolekcii v Shopify admine
   (Products → Collections → Sort), čo je natívne riešenie bez presmerovania. */

function boot(scope = document) {
  syncSearchPageCopy(scope);
  rescueEmptySearch();
  scope.querySelectorAll('[data-ao-collection-search]').forEach(initSearch);
  scope.querySelectorAll('[data-ao-browser-controls]').forEach(initControls);
  scope.querySelectorAll('[data-ao-browser-card]').forEach(initCard);
  scope.querySelectorAll('[data-ao-product-browser]').forEach(initPagination);
  setStickyOffset();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
else boot();
window.addEventListener('pageshow', () => boot());
